/**
 * Tiny client-side A/B bucketing — works without PostHog feature flags.
 *
 * Why not posthog.getFeatureFlag()?
 *   Our PostHog init uses `advanced_disable_decide: true` (see lib/sentry.js)
 *   to stay resilient against ad-blockers and strict CSP environments. That
 *   disables the /decide round-trip that feature flags depend on.
 *
 * What we do instead:
 *   1. Generate a stable `ab_vid` (visitor id) in localStorage on first visit.
 *   2. FNV-1a hash `${experiment}:${ab_vid}` → uniform 0..1 number.
 *   3. Map to a variant by cumulative weight.
 *   4. Register the chosen variant as a PostHog *super-property* so every
 *      subsequent track() call (clicks, signup_completed, etc.) carries the
 *      variant tag — making it filterable in any PostHog query / dashboard.
 *
 * Same statistical power as a true feature flag, with none of the failure
 * modes. Trade-off: variants are decided client-side, so you can't change
 * them remotely without a deploy (acceptable for marketing copy tests).
 */
import { posthog, track } from "@/lib/sentry";

const STORAGE_KEY = "afyanex_ab_vid";

function _genVisitorId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function _getVisitorId() {
  try {
    let v = localStorage.getItem(STORAGE_KEY);
    if (!v) {
      v = _genVisitorId();
      localStorage.setItem(STORAGE_KEY, v);
    }
    return v;
  } catch {
    // private mode / disabled storage — fall back to session-stable id
    return "no-storage";
  }
}

/** FNV-1a 32-bit. Deterministic, uniform-enough for bucketing. */
function _hash(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h / 0xffffffff;
}

/**
 * Pick a variant deterministically for this visitor + experiment.
 *
 * @param {string} experimentKey  Stable id, e.g. "hero_headline_v1"
 * @param {string[]} variants     Variant keys, e.g. ["control", "two_min"]
 * @param {number[]} [weights]    Cumulative weights matching variants; default
 *                                = equal split.
 * @returns {string} The chosen variant key.
 *
 * Side effects:
 *   - Stores the bucket in PostHog super-properties so downstream events
 *     carry `experiment_${experimentKey}: ${variant}`.
 *   - Fires `experiment_assigned` (idempotent — only first time per visitor
 *     for this experiment).
 */
export function pickVariant(experimentKey, variants, weights) {
  if (!Array.isArray(variants) || variants.length < 2) return variants?.[0] || "control";
  const w = (weights && weights.length === variants.length) ? weights : variants.map(() => 1);
  const total = w.reduce((a, b) => a + b, 0);
  const vid = _getVisitorId();
  const r = _hash(`${experimentKey}:${vid}`);
  let acc = 0;
  let chosen = variants[variants.length - 1];
  for (let i = 0; i < variants.length; i += 1) {
    acc += w[i] / total;
    if (r < acc) { chosen = variants[i]; break; }
  }
  // Register as super-property so every downstream event gets tagged.
  // Safe to call repeatedly — PostHog dedupes.
  try {
    posthog.register({ [`experiment_${experimentKey}`]: chosen });
  } catch {
    // posthog may not be initialized yet — track() will no-op safely
  }
  // Idempotent assigned event (once per visitor per experiment).
  try {
    const seenKey = `afyanex_ab_seen_${experimentKey}`;
    if (typeof localStorage !== "undefined" && !localStorage.getItem(seenKey)) {
      localStorage.setItem(seenKey, chosen);
      track("experiment_assigned", { experiment: experimentKey, variant: chosen });
    }
  } catch {
    // ignore
  }
  return chosen;
}
